// Состояние раздела «Правила» в адресе: фильтры, выбранная норма, точка
// «все правила здесь».
//
// Умолчания в адрес НЕ пишутся — тот же приём, что у «Буёв»: «Поделиться»
// иначе отдавал бы длинную ссылку, в которой ничего не выбрано.
//
// Точка — `plat`/`plon`, а НЕ `lat`/`lon`: центр карты пишется в тот же адрес
// ключами `lng`/`lat`/`zoom` (`map/mapView.ts`). На общем `lat` первый же сдвиг
// карты молча переписывал широту точки широтой центра, а ссылка `?lng=…&lat=…`
// не задавала точки вовсе. Старые `lat`+`lon` (ссылки из прошлых карточек)
// читаются, только если в адресе нет `lng`: иначе `lat` — это центр.

export interface RulesUrlState {
  /** `null` — каталог выбирает сервер по домену (Selara — Оман, а не ДВ). */
  region: string | null
  section: string | null
  kind: string | null
  fishingType: string | null
  species: string | null
  gear: string | null
  vesselLength: number | null
  date: string | null
  onlyActive: boolean
  q: string
  selected: string | null
  /** «Все правила здесь» — клик по карте или точка из карточки отсчёта. */
  at: { lat: number; lon: number } | null
  /** Радиус окрестности клика, м; `null` — сервер берёт ячейку GEBCO. */
  radius: number | null
  eez: boolean
  fd: boolean
}

export const DEFAULT_REGION: string | null = null

export const EMPTY_RULES_URL: RulesUrlState = {
  region: DEFAULT_REGION, section: null, kind: null, fishingType: null, species: null,
  gear: null, vesselLength: null, date: null, onlyActive: false, q: '', selected: null,
  at: null, radius: null, eez: false, fd: false,
}

export const RULES_URL_KEYS = [
  'region', 'section', 'kind', 'ft', 'sp', 'gear', 'len', 'date', 'active', 'q', 'norm',
  'plat', 'plon', 'pr', 'eez', 'fd',
]

/** Старые ключи точки: их снимают при записи, чтобы не задваивать точку. */
const LEGACY_POINT_KEYS = ['lon']

const str = (v: string | null): string | null => (v && v.trim() ? v.trim() : null)

function num(v: string | null, lo: number, hi: number): number | null {
  if (v == null || v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) && n >= lo && n <= hi ? n : null
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function pointOf(p: URLSearchParams): { lat: number; lon: number } | null {
  const lat = num(p.get('plat'), -90, 90)
  const lon = num(p.get('plon'), -180, 360)
  if (lat != null && lon != null) return { lat, lon }
  if (p.has('lng')) return null
  const oldLat = num(p.get('lat'), -90, 90)
  const oldLon = num(p.get('lon'), -180, 360)
  return oldLat != null && oldLon != null ? { lat: oldLat, lon: oldLon } : null
}

export function parseRulesUrl(p: URLSearchParams): RulesUrlState {
  const at = pointOf(p)
  const date = str(p.get('date'))
  return {
    region: str(p.get('region')) ?? DEFAULT_REGION,
    section: str(p.get('section')),
    kind: str(p.get('kind')),
    fishingType: str(p.get('ft')),
    species: str(p.get('sp')),
    gear: str(p.get('gear')),
    vesselLength: num(p.get('len'), 0, 500),
    // Ссылка приходит извне: битая дата читается как «сегодня», а не уходит на
    // сервер отказом 400.
    date: date && DATE_RE.test(date) ? date : null,
    onlyActive: p.get('active') === '1',
    q: p.get('q') ?? '',
    selected: str(p.get('norm')),
    // Половина точки — не точка: без долготы «все правила здесь» не спросить.
    at,
    radius: at ? num(p.get('pr'), 0, 100_000) : null,
    eez: p.get('eez') === '1',
    fd: p.get('fd') === '1',
  }
}

export function writeRulesUrl(s: RulesUrlState): URLSearchParams {
  const p = new URLSearchParams()
  if (s.region) p.set('region', s.region)
  if (s.section) p.set('section', s.section)
  if (s.kind) p.set('kind', s.kind)
  if (s.fishingType) p.set('ft', s.fishingType)
  if (s.species) p.set('sp', s.species)
  if (s.gear) p.set('gear', s.gear)
  if (s.vesselLength != null) p.set('len', String(s.vesselLength))
  if (s.date) p.set('date', s.date)
  if (s.onlyActive) p.set('active', '1')
  if (s.q.trim()) p.set('q', s.q.trim())
  if (s.selected) p.set('norm', s.selected)
  if (s.at) {
    p.set('plat', s.at.lat.toFixed(4))
    p.set('plon', s.at.lon.toFixed(4))
    if (s.radius != null) p.set('pr', String(Math.round(s.radius)))
  }
  if (s.eez) p.set('eez', '1')
  if (s.fd) p.set('fd', '1')
  return p
}

/** Адрес раздела «все правила здесь» — из карточки отсчёта в точке. */
/** Ключи, которые раздел снимает с адреса перед записью своих. */
export const RULES_URL_CLEARED = [...RULES_URL_KEYS, ...LEGACY_POINT_KEYS]

export function rulesHereHref(lat: number, lon: number): string {
  return `/rules?${writeRulesUrl({ ...EMPTY_RULES_URL, at: { lat, lon } }).toString()}`
}

/** Каталог из адреса не привязан к кабинету (сервер ответил `region_forbidden`):
 *  выбор сбрасывается, и сервер открывает каталог кабинета. Старая ссылка или
 *  сохранённый вид иначе оставляли раздел на плашке ошибки. */
export function isRegionForbidden(err: unknown): boolean {
  const d = (err as { status?: number; detail?: unknown } | null)
  if (!d || d.status !== 403) return false
  const detail = d.detail as { code?: string } | null
  return detail?.code === 'region_forbidden'
}
